/**
 * FolderTree Component
 * Displays a hierarchical tree of folders in a sidebar for easy navigation and drag-drop
 */

import { useState, useMemo } from "react";
import { useDroppable } from "@dnd-kit/core";
import {
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  Home,
} from "lucide-react";
import { FileNode } from "./types";

interface FolderTreeProps {
  nodes: Record<string, FileNode>;
  activeFolderId: string | null;
  onNavigate: (folderId: string | null) => void;
  className?: string;
}

interface FolderTreeItemProps {
  folder: FileNode | null; // null for root
  children: FileNode[];
  allNodes: Record<string, FileNode>;
  activeFolderId: string | null;
  onNavigate: (folderId: string | null) => void;
  level: number;
}

function FolderTreeItem({
  folder,
  children,
  allNodes,
  activeFolderId,
  onNavigate,
  level,
}: FolderTreeItemProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const folderId = folder?.id || null;
  const isActive = activeFolderId === folderId;
  const isRoot = folder === null;

  // Setup droppable for this folder
  const { setNodeRef, isOver } = useDroppable({
    id: folderId || "root",
    data: { type: "folder", node: folder },
  });

  // Get subfolders
  const subfolders = useMemo(() => {
    return children
      .filter((node) => node.type === "folder")
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [children]);

  const hasSubfolders = subfolders.length > 0;

  return (
    <div>
      <div
        ref={setNodeRef}
        onClick={() => {
          onNavigate(folderId);
          if (!isExpanded && hasSubfolders) {
            setIsExpanded(true);
          }
        }}
        className={`
          flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-colors group
          ${
            isActive
              ? "bg-blue-500/10 text-blue-600 dark:text-blue-400"
              : "hover:bg-gray-100 dark:hover:bg-gray-800"
          }
          ${isOver ? "bg-blue-500/20 ring-2 ring-blue-500" : ""}
        `}
        style={{ paddingLeft: `${level * 12 + 8}px` }}
      >
        {/* Expand/collapse chevron */}
        {hasSubfolders && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
          >
            {isExpanded ? (
              <ChevronDown className="w-3.5 h-3.5 dark:text-white" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5 dark:text-white" />
            )}
          </button>
        )}

        {/* Spacer if no children */}
        {!hasSubfolders && <div className="w-4" />}

        {/* Folder icon */}
        {isRoot ? (
          <Home className="w-4 h-4 flex-shrink-0 dark:text-white" />
        ) : isActive || isExpanded ? (
          <FolderOpen className="w-4 h-4 flex-shrink-0 dark:text-white" />
        ) : (
          <Folder className="w-4 h-4 flex-shrink-0 dark:text-white" />
        )}

        {/* Folder name */}
        <span
          className={`text-sm truncate flex-1 dark:text-white ${
            isActive ? "font-medium" : ""
          }`}
        >
          {isRoot ? "All Files" : folder!.name}
        </span>
      </div>

      {/* Render subfolders */}
      {isExpanded && hasSubfolders && (
        <div>
          {subfolders.map((subfolder) => {
            const subChildren = Object.values(allNodes).filter(
              (node) => node.parentId === subfolder.id,
            );
            return (
              <FolderTreeItem
                key={subfolder.id}
                folder={subfolder}
                children={subChildren}
                allNodes={allNodes}
                activeFolderId={activeFolderId}
                onNavigate={onNavigate}
                level={level + 1}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

export function FolderTree({
  nodes,
  activeFolderId,
  onNavigate,
  className = "",
}: FolderTreeProps) {
  // Get root-level children
  const rootChildren = useMemo(() => {
    return Object.values(nodes).filter((node) => node.parentId === null);
  }, [nodes]);

  return (
    <div
      className={`flex flex-col h-full bg-gray-50 dark:bg-gray-900/50 border-r border-gray-200 dark:border-gray-800 ${className}`}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
        <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          Folders
        </h3>
      </div>

      {/* Tree */}
      <div className="flex-1 overflow-y-auto p-2">
        <FolderTreeItem
          folder={null}
          children={rootChildren}
          allNodes={nodes}
          activeFolderId={activeFolderId}
          onNavigate={onNavigate}
          level={0}
        />
      </div>
    </div>
  );
}
